<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, inject } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useVisionAccess } from 'dashboard/composables/useVisionAccess';
import {
  SIDEPANEL_INJECTION_KEY,
  panelFromUISettings,
  uiSettingsForPanel,
} from './sidepanels';

const { uiSettings, updateUISettings } = useUISettings();

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

// Painel LOCAL de quem hospeda o switch. `null` (todo lugar menos a
// ConversationSheet do quadro) = comportamento de sempre, na preferência
// persistida do perfil.
//
// Existe porque abrir uma conversa em contexto transitório não pode
// reconfigurar a caixa de entrada: `uiSettings` é preferência do PERFIL, lida
// em toda conversa e em todo dispositivo.
const sidepanel = inject(SIDEPANEL_INJECTION_KEY, null);

// Vision é do administrador; agente só tem se um admin liberar tools no papel
// personalizado dele (o backend responde 403 pro resto): sem isto o botão
// abriria um painel que só sabe errar.
const { visionAllowed } = useVisionAccess();

// Copiloto e Visão são hospedados pelo layout da caixa de entrada
// (CopilotContainer/VisionContainer, que leem `uiSettings`), não pela
// ConversationSheet. Com estado local injetado, o botão deles só fechava o
// contato e não abria nada — botão que promete painel e entrega tela vazia.
const showCopilotTab = computed(
  () =>
    !sidepanel &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.CAPTAIN
    )
);

const showVisionTab = computed(
  () =>
    !sidepanel &&
    visionAllowed.value &&
    isFeatureEnabledonAccount.value(
      currentAccountId.value,
      FEATURE_FLAGS.VISION
    )
);

const activePanel = computed(() =>
  sidepanel ? sidepanel.value : panelFromUISettings(uiSettings.value)
);

const isContactSidebarOpen = computed(() => activePanel.value === 'contact');
const isCopilotPanelOpen = computed(() => activePanel.value === 'copilot');
const isVisionPanelOpen = computed(() => activePanel.value === 'vision');

const applyPanel = name => {
  if (sidepanel) {
    sidepanel.value = name;
    return;
  }
  updateUISettings(uiSettingsForPanel(name));
};

// `openPanel` abre e mantém aberto (botões de contato/copiloto/visão);
// `togglePanel` fecha se já estiver no ar (Alt+O).
const openPanel = name => applyPanel(name);
const togglePanel = name =>
  applyPanel(activePanel.value === name ? null : name);

const toggleConversationSidebarToggle = () => togglePanel('contact');
const handleConversationSidebarToggle = () => openPanel('contact');
const handleCopilotSidebarToggle = () => openPanel('copilot');
const handleVisionSidebarToggle = () => openPanel('vision');

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <div
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2 border border-n-weak rounded-full gap-2 p-1"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      ghost
      slate
      sm
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2': isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isCopilotPanelOpen,
      }"
      sm
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
    <Button
      v-if="showVisionTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.VISION')"
      ghost
      slate
      class="!rounded-full"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9': isVisionPanelOpen,
      }"
      sm
      icon="i-lucide-scan-search"
      @click="handleVisionSidebarToggle"
    />
  </div>
</template>
